import * as React from 'react';
import { AuthContext } from '../../contextProviders/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, Flex, FlexColLg, FlexEnd, FlexMdAlignCenter, Headline1, Headline2 } from '../../components/Elements';
import styled from 'styled-components';
import RadioFields, { RadioFieldOption } from 'components/forms/RadioFields';
import useNeveForm from 'components/forms/NeveForm';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import useModifyRequest from 'hooks/useModifyRequest';
import MainSection from 'components/sections/MainSection';
import strings from '../../strings/strings.json';
import { breakpoints, sizes } from 'theme';
import UserCreateOrganisationForm from './components/UserCreateOrganisationForm';
import CheckboxInput from 'components/forms/CheckboxInput';
import { MARKETING_TERMS_URL, TERMS_URL } from 'lib/_api-helpers';
import { StandardLink } from 'components/StandardLink';
import NavBackButton from 'components/buttons/NavBackButton';
import Button from 'components/buttons/Button';

enum ChooseRole {
    LEARNER = 0,
    ORGANISATION = 1,
}

export default function InitialLoginPage(): JSX.Element {
    const { state } = React.useContext(AuthContext);
    if (!!state) {
        const regex =
            /^\/learner\/enroll\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        const joinCourseBeforeSignUp = regex.test(state);
        if (joinCourseBeforeSignUp) {
            return (
                <MainSection>
                    <InitialLoginFormForEnrolment enrolCourseId={state.split('/')[3]} />
                </MainSection>
            );
        }
    }
    return (
        <MainSection>
            <InitialLoginForm />
        </MainSection>
    );
}

function InitialLoginForm(): JSX.Element {
    const navigate = useNavigate();
    const { refreshUserData } = React.useContext(AuthContext);
    const [error, setError] = React.useState<string>('');
    const [showCreateOrganisationForm, setShowCreateOrganisationForm] = React.useState<boolean>(false);
    const [marketingTermsAccepted, setMarketingTermsAccepted] = React.useState<boolean>(false);

    const { modifyData: addLearnerRole, loading: addingLearnerRole } = useModifyRequest(
        `users/me/assign-learner-role`,
        'PATCH',
    );

    const { register, handleSubmit } = useNeveForm<ChooseRoleAndAgreeToTermsInput>({
        type: null,
        agreeToTerms: false,
        marketingTermsAccepted: false,
    });

    const options: ChooseRoleOption[] = [
        {
            type: ChooseRole.LEARNER,
            name: strings.selectRolePage.radioOptions.learner.name,
            description: strings.selectRolePage.radioOptions.learner.description,
            aria: strings.selectRolePage.radioOptions.learner.aria,
        },
        {
            type: ChooseRole.ORGANISATION,
            name: strings.selectRolePage.radioOptions.organisation.name,
            description: strings.selectRolePage.radioOptions.organisation.description,
            aria: strings.selectRolePage.radioOptions.organisation.aria,
        },
    ];

    const radioFieldOptions: RadioFieldOption[] = options.map((option) => {
        return {
            value: option.type,
            title: option.name,
            description: option.description,
            aria: option.aria,
            inputProps: register('type'),
            name: 'type',
        };
    });

    async function submit(formData: ChooseRoleAndAgreeToTermsInput) {
        setError('');

        if (formData.type === null) {
            setError(strings.selectRolePage.error.role);
            return;
        } else if (formData.agreeToTerms === false) {
            setError(strings.selectRolePage.error.terms);
            return;
        }

        const selection = options.find((x) => +x.type === +formData.type!);
        const marketingConsentPayload = formData.marketingTermsAccepted ? { marketingTermsAccepted: true } : {};

        switch (selection!.type) {
            case ChooseRole.LEARNER:
                const { errors: addLearnerRoleErrors } = await addLearnerRole(marketingConsentPayload);
                if (addLearnerRoleErrors) {
                    setError(strings.selectRolePage.error.terms);
                    return;
                }
                await refreshUserData();
                navigate('/learner/courses');
                break;
            case ChooseRole.ORGANISATION:
                setMarketingTermsAccepted(formData.marketingTermsAccepted);
                setShowCreateOrganisationForm(true);
                break;
        }
    }

    if (showCreateOrganisationForm) {
        return (
            <CreateOrgBase>
                <CreateOrgContainer>
                    <Headline1>{strings.selectRolePage.welcomeHeader}</Headline1>
                    <CreateOrganisationFormContent>
                        <NavBackButton
                            backButtonTitle={'Select role'}
                            backButtonAria={'Back to select role'}
                            backButtonHref={''}
                            buttonFunctionOverride={() => setShowCreateOrganisationForm(false)}
                        />
                        <UserCreateOrganisationForm
                            showHeadlineText
                            marketingTermsAccepted={marketingTermsAccepted}
                        />
                    </CreateOrganisationFormContent>
                </CreateOrgContainer>
            </CreateOrgBase>
        );
    }
    return (
        <SelectRoleForm onSubmit={handleSubmit(submit)}>
            <Headline1>{strings.selectRolePage.welcomeHeader}</Headline1>
            <FormContent>
                <Headline2>{strings.selectRolePage.chooseRoleHeader}</Headline2>
                <FlexColLg>
                    <RadioFields options={radioFieldOptions} />
                    <Grid $showerror={!!error}>
                        <TermsContainer>
                            <TermsAndCheckboxRow>
                                <StandardLink linkUrl={TERMS_URL} linkText={strings.selectRolePage.terms.linkText} />
                                <CheckboxInput
                                    id={'terms'}
                                    // value={''}
                                    aria={strings.selectRolePage.terms.aria}
                                    {...register('agreeToTerms')}
                                />
                            </TermsAndCheckboxRow>
                            <TermsAndCheckboxRow>
                                <StandardLink
                                    linkUrl={MARKETING_TERMS_URL}
                                    linkText={strings.selectRolePage.marketingTerms.linkText}
                                />
                                <CheckboxInput
                                    id={'marketingTerms'}
                                    aria={strings.selectRolePage.marketingTerms.aria}
                                    {...register('marketingTermsAccepted')}
                                />
                            </TermsAndCheckboxRow>
                        </TermsContainer>
                        <ContinueButton
                            type="submit"
                            title={strings.selectRolePage.button.title}
                            aria={strings.selectRolePage.button.aria}
                            submitting={addingLearnerRole}
                        />
                        {error && (
                            <ErrorContainer>
                                <ErrorNotifierSlim description={error} />
                            </ErrorContainer>
                        )}
                    </Grid>
                </FlexColLg>
            </FormContent>
        </SelectRoleForm>
    );
}

type InitialLoginFormForEnrolmentProps = {
    enrolCourseId: string;
};

function InitialLoginFormForEnrolment({ enrolCourseId }: InitialLoginFormForEnrolmentProps): JSX.Element {
    const navigate = useNavigate();
    const { refreshUserData } = React.useContext(AuthContext);
    const [error, setError] = React.useState<string>('');

    const { modifyData: addLearnerRole, loading: addingLearnerRole } = useModifyRequest(
        `users/me/assign-learner-role`,
        'PATCH',
    );
    const { modifyData: enrolLearnerOnCourse, loading: enrolling } = useModifyRequest(
        `courses/enroll/${enrolCourseId}`,
        'POST',
    );

    const { register, handleSubmit } = useNeveForm<AgreeToTermsInput>({
        agreeToTerms: false,
        marketingTermsAccepted: false,
    });

    async function submit(formData: AgreeToTermsInput) {
        setError('');

        if (formData.agreeToTerms === false) {
            setError(strings.selectRolePage.error.terms);
            return;
        }

        const marketingConsentPayload = formData.marketingTermsAccepted ? { marketingTermsAccepted: true } : {};

        const { errors: addLearnerRoleErrors } = await addLearnerRole(marketingConsentPayload);
        if (addLearnerRoleErrors) {
            setError(strings.selectRolePage.error.apiError);
            return;
        }
        const { errors: enrolLearnerOnCourseErrors } = await enrolLearnerOnCourse({});
        if (enrolLearnerOnCourseErrors) {
            setError(strings.selectRolePage.error.apiError);
            return;
        }

        await refreshUserData();
        navigate('/learner/courses');
    }

    return (
        <SelectRoleForm onSubmit={handleSubmit(submit)}>
            <Headline1>{strings.selectRolePage.welcomeHeader}</Headline1>
            <FormContent>
                <Grid $showerror={!!error}>
                    <TermsContainer>
                        <TermsAndCheckboxRow>
                            <StandardLink linkUrl={TERMS_URL} linkText={strings.selectRolePage.terms.linkText} />
                            <CheckboxInput
                                // value={''}
                                id="terms"
                                aria={strings.selectRolePage.terms.aria}
                                {...register('agreeToTerms')}
                            />
                        </TermsAndCheckboxRow>
                        <TermsAndCheckboxRow>
                            <StandardLink
                                linkUrl={MARKETING_TERMS_URL}
                                linkText={strings.selectRolePage.marketingTerms.linkText}
                            />
                            <CheckboxInput
                                id="marketingTerms"
                                aria={strings.selectRolePage.marketingTerms.aria}
                                {...register('marketingTermsAccepted')}
                            />
                        </TermsAndCheckboxRow>
                    </TermsContainer>
                    <ContinueButton
                        type="submit"
                        title={strings.selectRolePage.button.title}
                        aria={strings.selectRolePage.button.aria}
                        submitting={addingLearnerRole || enrolling}
                    />
                    {error && (
                        <ErrorContainer>
                            <ErrorNotifierSlim description={error} />
                        </ErrorContainer>
                    )}
                </Grid>
            </FormContent>
        </SelectRoleForm>
    );
}

type ChooseRoleOption = {
    type: ChooseRole;
    name: string;
    description: string;
    aria: string;
};

type ChooseRoleAndAgreeToTermsInput = {
    type: 0 | 1 | null;
    agreeToTerms: boolean;
    marketingTermsAccepted: boolean;
};

type AgreeToTermsInput = {
    agreeToTerms: boolean;
    marketingTermsAccepted: boolean;
};

const CreateOrgContainer = styled(FlexColLg)`
    margin: ${sizes.spacingLg} auto;
    width: 640px;
`;

const CreateOrgBase = styled(Flex)`
    justify-content: center;
    width: 100%;
`;

const Grid = styled.div<{ $showerror: boolean }>`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    row-gap: ${({ $showerror }) => ($showerror ? sizes.spacingRg : 0)};
    column-gap: ${sizes.spacingLg};

    grid-template-areas:
        'terms terms'
        '. button'
        'error error';

    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
        row-gap: ${sizes.spacingRg};
        column-gap: 0;

        grid-template-areas:
            'terms'
            'button'
            'error';
    }
`;

const TermsContainer = styled(FlexColLg)`
    grid-area: terms;
`;

const TermsAndCheckboxRow = styled(FlexMdAlignCenter)`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: ${sizes.spacingMd};
    width: 100%;
`;

const ContinueButton = styled(Button)`
    grid-area: button;
    justify-self: end;
    margin-top: ${sizes.spacingMd};
    min-width: 160px;
`;

const ErrorContainer = styled(FlexEnd)`
    grid-area: error;

    @media (max-width: ${breakpoints.md}) {
        justify-content: start;
    }
`;

const FormContent = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
`;

const CreateOrganisationFormContent = styled(FormContent)`
    gap: ${sizes.spacingRg};
`;

const SelectRoleForm = styled.form`
    display: flex;
    flex-direction: column;
    max-width: 640px;
    gap: ${sizes.spacingLg};
    width: 100%;
    margin: ${sizes.spacingLg} auto;
`;
